import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  ArrowRight, Heart, Sparkles, MessageSquare, Briefcase,
  Target, BookOpen, Globe, Volume2, Shield, Fingerprint, Users,
  Calendar, Zap, Download, Lock, BarChart3, Mic, Clock, Palette,
  Bell, Check, Loader2
} from "lucide-react";

/* ─── Waitlist Signup ─── */
function WaitlistSection() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email, source: "features_page" });
      setDone(true);
      toast.success(data.message || "You're on the list!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <section className="py-24 px-6 lg:px-12" style={{ background: "#0A0E1A" }} data-testid="waitlist-section">
      <div className="max-w-2xl mx-auto text-center">
        <Reveal>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ background: "rgba(79,140,255,0.08)" }}>
            <Bell className="w-7 h-7 text-[#4F8CFF]" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
            New guides are on the way
          </h2>
          <p className="text-[#A1A5B3] mb-10 leading-relaxed max-w-md mx-auto">
            More specialist guides and features are coming to Omni. Join the list and be the first to know the moment they launch.
          </p>
        </Reveal>

        <Reveal delay={100}>
          {done ? (
            <div className="inline-flex items-center gap-3 px-6 py-4 rounded-full border" style={{ borderColor: "rgba(52,211,153,0.3)", background: "rgba(52,211,153,0.06)" }} data-testid="waitlist-done">
              <Check className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-emerald-400 font-medium">You're on the list — we'll notify you first</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto">
              <div className="relative flex-1 w-full">
                <Bell className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E7385]" />
                <input
                  data-testid="waitlist-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full pl-11 pr-4 rounded-full text-sm text-white placeholder-[#6E7385] focus:outline-none transition-all"
                  style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.1)", height: "52px" }}
                />
              </div>
              <button
                data-testid="waitlist-notify-btn"
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 rounded-full text-white font-medium text-sm transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 flex-shrink-0"
                style={{ background: "#4F8CFF", height: "52px", boxShadow: "0 0 20px rgba(79,140,255,0.2)" }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {submitting ? "Joining..." : "Notify me"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay * 0.001, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const MODULES_DETAIL = [
  {
    icon: Heart, name: "The Friend", color: "#E31212",
    tagline: "Your always-available confidant",
    details: [
      "Casual conversation that feels natural and warm",
      "Emotional support grounded in your personal history",
      "Remembers your story, your preferences, your journey",
      "Available 24/7 — no appointments, no waiting rooms",
    ],
  },
  {
    icon: Sparkles, name: "The Mentor", color: "#F5A623",
    tagline: "Wisdom from every tradition",
    details: [
      "Guidance aligned with your faith, philosophy, or worldview",
      "Draws from scripture, Stoicism, humanism, and more",
      "Culturally sensitive — never imposes a single perspective",
      "Helps you find meaning in life's biggest questions",
    ],
  },
  {
    icon: MessageSquare, name: "The Social Guide", color: "#34D399",
    tagline: "Navigate any social situation",
    details: [
      "Role-play difficult conversations before they happen",
      "Practice conflict resolution and boundary-setting",
      "Decode social dynamics with cultural awareness",
      "Build confidence for interviews, dates, and negotiations",
    ],
  },
  {
    icon: Briefcase, name: "The Job Guardian", color: "#A78BFA",
    tagline: "Your career accelerator",
    details: [
      "Resume and cover letter optimization with AI review",
      "Mock interview sessions with real-time feedback",
      "Salary negotiation strategies and talking points",
      "Workplace navigation — handling politics, promotions, and pivots",
    ],
  },
  {
    icon: Target, name: "The Life Coach", color: "#22D3EE",
    tagline: "Turn intentions into actions",
    details: [
      "Transform big goals into daily micro-actions",
      "Habit tracking with streaks and accountability",
      "Weekly check-ins with personalized progress reports",
      "Motivation calibrated to your personality and values",
    ],
  },
  {
    icon: BookOpen, name: "The Storyteller", color: "#FB923C",
    tagline: "Wisdom through narrative",
    details: [
      "Personalized fables drawn from your cultural heritage",
      "Bedtime stories for children with moral lessons",
      "Creative writing prompts and collaborative storytelling",
      "Literature recommendations based on your mood and interests",
    ],
  },
];

const PLATFORM_FEATURES = [
  { icon: Volume2, title: "Voice Conversations", desc: "Speak naturally. Hear lifelike responses. Hands-free interaction in any language." },
  { icon: Mic, title: "Speech-to-Text", desc: "Dictate messages using Whisper-powered transcription. Accurate across 113 languages." },
  { icon: BarChart3, title: "Mood Tracking & Heatmaps", desc: "Log emotions daily. Visualize patterns with beautiful heatmaps and weekly AI insights." },
  { icon: Target, title: "Goals & Streaks", desc: "Set goals, build habits, track streaks. AI coaching keeps you accountable." },
  { icon: Calendar, title: "Daily Recaps", desc: "AI-written summaries of your day's conversations, moods, and progress." },
  { icon: Fingerprint, title: "Biometric Login", desc: "Face ID, fingerprint, Windows Hello. Enterprise-grade security with zero friction." },
  { icon: Users, title: "Family & Team Plans", desc: "Multi-member subscriptions with age-appropriate AI and parental controls." },
  { icon: Zap, title: "Smart Cultural Greetings", desc: "Greetings aware of your language, culture, time of day, and significant occasions." },
  { icon: Download, title: "Smart Backup & Export", desc: "Auto-backup conversations, mood logs, and goals. Export your data anytime as JSON or PDF." },
  { icon: Globe, title: "113 Languages", desc: "Six free. 107+ premium. Full cultural context, not just word-for-word translation." },
  { icon: Lock, title: "End-to-End Encryption", desc: "Your conversations are private. We can't read them. We never sell your data." },
  { icon: Clock, title: "AI Memory", desc: "Omni remembers your preferences, history, and growth over time. Unlimited for paid users." },
  { icon: Palette, title: "5 Themes & Accessibility", desc: "Dark mode, high contrast, custom colors, font scaling, and reduced motion support." },
  { icon: Shield, title: "GDPR Compliant", desc: "Full data export, account deletion, and transparent privacy practices." },
  { icon: Heart, title: "Testimonials & Reviews", desc: "A community of users sharing their journeys. Read stories. Add your own." },
];

export default function FeaturesPage() {
  return (
    <>
      {/* ═══════ HERO ═══════ */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 px-6 lg:px-12 overflow-hidden" data-testid="features-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-0 w-[600px] h-[500px]" style={{ background: "radial-gradient(circle, rgba(227,18,18,0.08) 0%, transparent 65%)" }} />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-6">Features</p>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05] mb-8"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Six minds.<br />
              <span className="text-[#E31212]">Infinite</span> possibilities.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg text-[#A1A5B3] max-w-2xl leading-relaxed">
              Each module is a distinct AI personality, trained for a specific domain of your life. 
              Together, they form the most comprehensive AI companion ever built.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════ MODULE DEEP DIVES ═══════ */}
      <section className="pb-20 px-6 lg:px-12" data-testid="features-modules">
        <div className="max-w-5xl mx-auto space-y-6">
          {MODULES_DETAIL.map((mod, i) => {
            const Icon = mod.icon;
            const isEven = i % 2 === 0;
            return (
              <Reveal key={mod.name} delay={i * 40}>
                <div className={`rounded-2xl border p-8 md:p-10 flex flex-col ${isEven ? "md:flex-row" : "md:flex-row-reverse"} items-start gap-8`}
                  style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${mod.color}12` }}>
                      <Icon className="w-8 h-8" style={{ color: mod.color }} strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold tracking-tight mb-1 text-white"
                      style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
                      {mod.name}
                      {mod.soon && <span className="ml-3 align-middle text-[10px] not-italic font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "#4F8CFF22", color: "#4F8CFF" }}>Coming Soon</span>}
                    </h3>
                    <p className="text-sm text-[#6E7385] mb-5">{mod.tagline}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {mod.details.map((d, j) => (
                        <div key={j} className="flex items-start gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: mod.color }} />
                          <p className="text-sm text-[#A1A5B3] leading-relaxed">{d}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ═══════ PLATFORM FEATURES ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" style={{ background: "#0A0E1A" }} data-testid="features-platform">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] mb-4">Platform</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-16"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Built for the real world
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PLATFORM_FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 40} className="rounded-2xl p-6 border transition-all duration-500 hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.01)", borderColor: "rgba(255,255,255,0.06)" }}>
                <f.icon className="w-5 h-5 text-[#F5A623] mb-4" strokeWidth={1.5} />
                <h3 className="text-sm font-medium tracking-tight mb-1.5 text-white">{f.title}</h3>
                <p className="text-xs text-[#6E7385] leading-relaxed">{f.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ Waitlist ═══════ */}
      <WaitlistSection />

      {/* ═══════ CTA ═══════ */}
      <section className="py-28 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Ready to meet your companion?
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-lg text-[#A1A5B3] mb-10">Start free. No credit card required. Upgrade anytime.</p>
          </Reveal>
          <Reveal delay={200}>
            <Link to="/auth" data-testid="features-cta-btn">
              <button className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-white font-medium text-lg transition-all duration-300 hover:-translate-y-1"
                style={{ background: "#E31212", boxShadow: "0 0 40px rgba(227,18,18,0.35)" }}>
                Get Started Free <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
